const ACCESS_TOKEN_KEY = "AccessToken";
const REFRESH_TOKEN_KEY = "RefreshToken";
const ID_TOKEN_KEY = "IdToken";
const IMPERSONATOR_ACCESS_TOKEN_KEY = "ImpersonatorAccessToken";
const IMPERSONATOR_REFRESH_TOKEN_KEY = "ImpersonatorRefreshToken";
const IMPERSONATED_USER_LABEL_KEY = "ImpersonatedUserLabel";
const EXPIRY_LEEWAY_MS = 30000;

class AuthService {
    async getAccessToken(): Promise<string | null> {
        return localStorage.getItem(ACCESS_TOKEN_KEY);
    }

    async getRefreshToken(): Promise<string | null> {
        return localStorage.getItem(REFRESH_TOKEN_KEY);
    }

    async getIdToken(): Promise<string | null> {
        return localStorage.getItem(ID_TOKEN_KEY);
    }

    async setAccessToken(token: string): Promise<void> {
        localStorage.setItem(ACCESS_TOKEN_KEY, token);
    }

    async setRefreshToken(token: string): Promise<void> {
        localStorage.setItem(REFRESH_TOKEN_KEY, token);
    }

    async setIdToken(token: string): Promise<void> {
        localStorage.setItem(ID_TOKEN_KEY, token);
    }

    async logout(): Promise<void> {
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem(ID_TOKEN_KEY);
        localStorage.removeItem(IMPERSONATOR_ACCESS_TOKEN_KEY);
        localStorage.removeItem(IMPERSONATOR_REFRESH_TOKEN_KEY);
        localStorage.removeItem(IMPERSONATED_USER_LABEL_KEY);
        window.dispatchEvent(new Event("storage"));
    }

    async beginImpersonation(
        impersonatorAccessToken: string,
        impersonatorRefreshToken: string,
        impersonatedUserLabel: string,
    ): Promise<void> {
        localStorage.setItem(IMPERSONATOR_ACCESS_TOKEN_KEY, impersonatorAccessToken);
        localStorage.setItem(IMPERSONATOR_REFRESH_TOKEN_KEY, impersonatorRefreshToken);
        localStorage.setItem(IMPERSONATED_USER_LABEL_KEY, impersonatedUserLabel);
    }

    isImpersonating(): boolean {
        return localStorage.getItem(IMPERSONATOR_REFRESH_TOKEN_KEY) !== null;
    }

    getImpersonatedUserLabel(): string | null {
        return localStorage.getItem(IMPERSONATED_USER_LABEL_KEY);
    }

    async endImpersonation(): Promise<void> {
        const impersonatorAccessToken = localStorage.getItem(IMPERSONATOR_ACCESS_TOKEN_KEY);
        const impersonatorRefreshToken = localStorage.getItem(IMPERSONATOR_REFRESH_TOKEN_KEY);
        if (impersonatorAccessToken) {
            localStorage.setItem(ACCESS_TOKEN_KEY, impersonatorAccessToken);
        }
        if (impersonatorRefreshToken) {
            localStorage.setItem(REFRESH_TOKEN_KEY, impersonatorRefreshToken);
        }
        localStorage.removeItem(IMPERSONATOR_ACCESS_TOKEN_KEY);
        localStorage.removeItem(IMPERSONATOR_REFRESH_TOKEN_KEY);
        localStorage.removeItem(IMPERSONATED_USER_LABEL_KEY);
        window.dispatchEvent(new Event("storage"));
    }

    getBearerToken(): string | null {
        const token = localStorage.getItem(ACCESS_TOKEN_KEY);
        return token ? `Bearer ${token}` : null;
    }

    isAccessTokenExpired(): boolean {
        const token = localStorage.getItem(ACCESS_TOKEN_KEY);
        if (!token) {
            return true;
        }

        const payloadSegment = token.split(".")[1];
        if (!payloadSegment) {
            return true;
        }

        try {
            const payload = JSON.parse(atob(payloadSegment.replace(/-/g, "+").replace(/_/g, "/"))) as {
                exp?: number;
            };
            if (typeof payload.exp !== "number") {
                return false;
            }
            return Date.now() >= payload.exp * 1000 - EXPIRY_LEEWAY_MS;
        } catch {
            return true;
        }
    }
}

export const authService = new AuthService();
